/**
 * Categorías oficiales de ElDescuadre y clasificación automática.
 *
 * DECISIÓN DE DISEÑO: se conservan **exactamente las 7 categorías de gasto**
 * que ya usaban Cuentia y Cuentify, con sus mismos criterios, porque son la
 * columna vertebral de todos los reportes. Se añaden las **5 categorías de
 * ingreso** que solo tenía Cuentify.
 *
 * La clasificación automática es **explicable**: devuelve siempre el motivo
 * (`reason`) y un nivel de confianza, y nunca decide en silencio. Eso es lo
 * contrario de la caja negra, y es lo que permite al usuario corregir y
 * aprender.
 *
 * Reglas finas que se respetan (venían documentadas en las dos aplicaciones y
 * son decisiones de producto, no casualidades):
 *
 *  - **Regla de oro del café**: el café *en grano o molido* para preparar en
 *    casa es **Mercado**; el café *ya preparado* (tinto, capuchino, latte,
 *    expreso) es **Gasto Hormiga**.
 *  - **«papa» frente a «papas»**: el tubérculo es **Mercado**; las papitas de
 *    paquete son **Gasto Hormiga**. Por eso se compara por palabras completas y
 *    nunca por trozos de texto (si no, «pan» coincidiría con «pantalón»).
 *  - El **licor y la cerveza** van a **Restaurantes y Ocio**, no a Mercado.
 */
/**
 * Las 7 categorías de gasto. Los nombres son literalmente los oficiales que ya
 * usaba Cuentia, porque son los que el usuario reconoce.
 */
export const EXPENSE_CATEGORIES = [
    {
        code: 'FIXED_HOME',
        order: 1,
        name: 'Gastos Fijos / Hogar',
        hint: 'Arriendo, administración, servicios públicos, aseo del hogar.',
        color: '#0C5BA8',
        icon: 'Home',
        kind: 'expense',
    },
    {
        code: 'MARKET',
        order: 2,
        name: 'Mercado / Alimentación en casa',
        hint: 'Mercado y despensa para preparar en casa. Café en grano o molido va aquí.',
        color: '#10B981',
        icon: 'ShoppingBasket',
        kind: 'expense',
    },
    {
        code: 'TRANSPORT',
        order: 3,
        name: 'Transporte / Movilidad',
        hint: 'Gasolina, bus, taxi, app de transporte, peajes, parqueadero.',
        color: '#4DEDFE',
        icon: 'Car',
        kind: 'expense',
    },
    {
        code: 'HORMIGA',
        order: 4,
        name: 'Gastos Hormiga',
        hint: 'Lo pequeño y diario: mecato, tinto preparado, dulces. Se acumula sin sentirlo.',
        color: '#ECD082',
        icon: 'Coffee',
        kind: 'expense',
    },
    {
        code: 'RESTAURANT_LEISURE',
        order: 5,
        name: 'Restaurantes y Ocio',
        hint: 'Comer fuera, domicilios, cine y licor (cerveza, aguardiente, vino).',
        color: '#EB525B',
        icon: 'UtensilsCrossed',
        kind: 'expense',
    },
    {
        code: 'SUBSCRIPTIONS',
        order: 6,
        name: 'Suscripciones Digitales',
        hint: 'Netflix, Spotify, planes de música, nube, servicios recurrentes digitales.',
        color: '#8B5CF6',
        icon: 'Tv',
        kind: 'expense',
    },
    {
        code: 'FINANCIAL',
        order: 7,
        name: 'Gastos Financieros / Intereses',
        hint: 'Cuota de manejo, intereses, comisiones, seguros y el 4x1000.',
        color: '#F07B4E',
        icon: 'Percent',
        kind: 'expense',
    },
];
/** Las 5 categorías de ingreso. */
export const INCOME_CATEGORIES = [
    {
        code: 'SALARY',
        order: 1,
        name: 'Salario / Nómina',
        hint: 'Pago de sueldo o nómina.',
        color: '#10B981',
        icon: 'BadgeDollarSign',
        kind: 'income',
    },
    {
        code: 'FREELANCE',
        order: 2,
        name: 'Honorarios / Independiente',
        hint: 'Trabajos por contrato, servicios prestados, honorarios.',
        color: '#20A9E6',
        icon: 'Briefcase',
        kind: 'income',
    },
    {
        code: 'BUSINESS',
        order: 3,
        name: 'Ventas / Negocio',
        hint: 'Ingresos de un negocio propio o ventas.',
        color: '#4DEDFE',
        icon: 'Store',
        kind: 'income',
    },
    {
        code: 'INVESTMENT',
        order: 4,
        name: 'Rendimientos',
        hint: 'Intereses ganados, dividendos, rendimientos de inversiones.',
        color: '#DAB56A',
        icon: 'TrendingUp',
        kind: 'income',
    },
    {
        code: 'OTHER_INCOME',
        order: 5,
        name: 'Otros ingresos',
        hint: 'Préstamos recibidos, devoluciones, regalos, recibos varios.',
        color: '#6E85A6',
        icon: 'PlusCircle',
        kind: 'income',
    },
];
/** Catálogo completo. */
export const ALL_CATEGORIES = [...EXPENSE_CATEGORIES, ...INCOME_CATEGORIES];
const CATEGORY_BY_CODE = new Map(ALL_CATEGORIES.map((c) => [c.code, c]));
/** Devuelve la categoría por su código, o `undefined` si no existe. */
export function categoryByCode(code) {
    return CATEGORY_BY_CODE.get(code);
}
/** Devuelve la categoría por su código y lanza si no existe. Útil en el backend. */
export function requireCategory(code) {
    const found = categoryByCode(code);
    if (!found)
        throw new Error(`Categoría desconocida: «${code}».`);
    return found;
}
/** ¿Es un código de gasto válido? */
export function isExpenseCategory(code) {
    return typeof code === 'string' && CATEGORY_BY_CODE.get(code)?.kind === 'expense';
}
/** ¿Es un código de ingreso válido? */
export function isIncomeCategory(code) {
    return typeof code === 'string' && CATEGORY_BY_CODE.get(code)?.kind === 'income';
}
/** Normaliza texto: minúsculas, sin tildes, solo palabras y números separados por espacios. */
export function normalizeText(input) {
    return input
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
}
/**
 * Reglas de clasificación, **en orden de prioridad**. El orden importa: las
 * reglas más específicas van primero. Por ejemplo, «cafe en grano» debe
 * evaluarse antes que «cafe», y «papas» antes que «papa».
 */
const CLASSIFICATION_RULES = [
    {
        category: 'FINANCIAL',
        terms: [
            'interes', 'intereses', 'comision', 'comisiones', 'cuota de manejo', 'cuota manejo',
            '4x1000', 'cuatro por mil', 'gravamen', 'gmf', 'seguro', 'seguros', 'poliza',
            'mora', 'sobregiro', 'avance', 'amortizacion', 'credito', 'fiduciario',
        ],
        confidence: 0.9,
        reason: 'Es un costo del sistema financiero (interés, comisión, seguro o gravamen).',
    },
    {
        category: 'SUBSCRIPTIONS',
        terms: [
            'netflix', 'spotify', 'disney', 'disney+', 'hbo', 'max', 'prime video', 'amazon prime',
            'youtube premium', 'suscripcion', 'suscripciones', 'icloud', 'google one', 'dropbox',
            'chatgpt', 'canva', 'adobe', 'paramount', 'crunchyroll', 'deezer', 'apple music',
            'microsoft 365', 'office 365', 'antivirus',
        ],
        confidence: 0.9,
        reason: 'Es un servicio digital de cobro recurrente.',
    },
    {
        category: 'TRANSPORT',
        terms: [
            'gasolina', 'combustible', 'diesel', 'acpm', 'uber', 'didi', 'cabify', 'indriver',
            'taxi', 'transmilenio', 'sitp', 'metro', 'metroplus', 'transcaribe', 'megabus',
            'parqueadero', 'parking', 'peaje', 'pasaje', 'bus', 'buseta', 'moto', 'moto taxi',
            'mototaxi', 'bicicleta', 'llanta', 'aceite de motor', 'tecnicomecanica', 'soat',
            'pico y placa', 'lavadero', 'grúa', 'grua',
        ],
        confidence: 0.88,
        reason: 'Es un gasto de movilidad o transporte.',
    },
    {
        category: 'RESTAURANT_LEISURE',
        terms: [
            'cerveza', 'polas', 'pola', 'licor', 'aguardiente', 'ron', 'whisky', 'whiskey', 'vodka',
            'tequila', 'vino', 'coctel', 'cocktail', 'bar', 'discoteca', 'cantina', 'estanco',
            'restaurante', 'corrientazo', 'almuerzo', 'cena', 'desayuno fuera', 'brunch', 'parrilla',
            'pizza', 'hamburguesa', 'perro caliente', 'sushi', 'pollo asado', 'broaster', 'asados',
            'rappi', 'domicilio', 'ifood', 'cine', 'cinemark', 'cinépolis', 'cinepolis', 'teatro',
            'concierto', 'bolera', 'billar', 'heladeria', 'crepes', 'crepes y waffles',
        ],
        confidence: 0.86,
        reason: 'Es comida fuera de casa, entretenimiento o licor.',
    },
    {
        category: 'HORMIGA',
        terms: [
            'mecato', 'mecatiar', 'papitas', 'papas fritas', 'chitos', 'chocorramo', 'chocolatina',
            'chocolatinas', 'dulce', 'dulces', 'chicle', 'chicles', 'galleta', 'galletas', 'snack',
            'snacks', 'gaseosa', 'gaseosas', 'chocolate', 'helado', 'helados', 'mani', 'crispetas',
            'tinto', 'capuchino', 'latte', 'expreso', 'espresso', 'mocaccino', 'frappuccino',
            'cafe preparado', 'cafe con leche', 'cafeteria', 'cafeterias', 'aguapanela',
            'aromatica', 'pastelito',
            'empanada', 'arepa de huevo', 'buñuelo', 'bunuelo', 'almojabana', 'pandebono',
        ],
        confidence: 0.88,
        reason: 'Es un gasto pequeño y frecuente del día a día.',
    },
    {
        category: 'MARKET',
        terms: [
            'mercado', 'supermercado', 'despensa', 'exito', 'carulla', 'd1', 'ara', 'olimpica',
            'jumbo', 'alkosto', 'makro', 'surtimax', 'super inter', 'la 14', 'euro',
            'leche', 'huevo', 'huevos', 'pan', 'panela', 'arroz', 'harina', 'azucar', 'sal', 'aceite',
            'papa', 'papas criollas', 'yuca', 'platano', 'maduro', 'tomate', 'cebolla', 'zanahoria',
            'frijol', 'lenteja', 'garbanzo', 'pasta', 'espagueti', 'avena', 'cereal', 'atun',
            'sardina', 'pollo', 'carne', 'cerdo', 'pescado', 'res', 'molida', 'salchicha', 'jamon',
            'queso', 'cuajada', 'yogurt', 'mantequilla', 'crema de leche', 'fruta', 'frutas',
            'verdura', 'verduras', 'legumbre', 'naranja', 'banano', 'manzana', 'mandarina', 'limon',
            // Ojo: el «café» suelto NO se lista aquí a propósito. Es ambiguo (¿grano o
            // taza?) y lo resuelve la rama explícita de más abajo, que además consulta
            // el importe. Si se listara, esa rama nunca se alcanzaría.
            'cafe en grano', 'cafe molido', 'cafe grano', 'cafe instantaneo', 'cafe de grano',
        ],
        confidence: 0.82,
        reason: 'Son alimentos o artículos de despensa para preparar en casa.',
    },
    {
        category: 'FIXED_HOME',
        terms: [
            'arriendo', 'alquiler', 'administracion', 'conjunto', 'servicios', 'luz', 'energia',
            'agua', 'acueducto', 'gas', 'alcantarillado', 'internet', 'claro', 'movistar', 'tigo',
            'wom', 'etb', 'directv', 'plan de datos', 'telefono', 'celular',
            'aseo', 'detergente', 'jabon', 'shampoo', 'champu', 'acondicionador', 'papel higienico',
            'limpieza', 'blanqueador', 'suavizante', 'escola', 'trapero', 'bolsas de basura',
            'predial', 'impuesto', 'aseguradora de hogar', 'reparacion', 'plomero', 'electricista',
            'muebles', 'colchon', 'vajilla', 'hogar', 'casa',
        ],
        confidence: 0.84,
        reason: 'Es un gasto fijo del hogar o un servicio público.',
    },
];
/** Umbral por debajo del cual la interfaz pide confirmación explícita. */
export const CONFIDENCE_CONFIRMATION_THRESHOLD = 0.6;
/**
 * Clasifica un texto libre en una de las 7 categorías de gasto.
 *
 * @param text   Descripción escrita por la persona («papitas del descanso»).
 * @param options `amount` permite afinar casos ambiguos como un «café» suelto.
 * @returns La categoría sugerida, o `null` si no hay ninguna pista suficiente.
 */
export function classifyExpense(text, options = {}) {
    const normalized = normalizeText(text);
    if (!normalized)
        return null;
    const padded = ` ${normalized} `;
    const tokens = new Set(normalized.split(' '));
    for (const rule of CLASSIFICATION_RULES) {
        for (const term of rule.terms) {
            const normalizedTerm = normalizeText(term);
            const matches = normalizedTerm.includes(' ')
                ? padded.includes(` ${normalizedTerm} `)
                : tokens.has(normalizedTerm);
            if (matches) {
                return { code: rule.category, confidence: rule.confidence, reason: rule.reason };
            }
        }
    }
    // Caso ambiguo deliberado: un «café» suelto. Si sabemos el importe, lo
    // resolvemos con criterio: un café barato es hormiga; una compra grande de
    // café es despensa. Si no lo sabemos, se deja que el usuario decida.
    if (tokens.has('cafe') || tokens.has('cafes')) {
        if (options.amount !== undefined && options.amount > 0 && options.amount <= 15_000) {
            return {
                code: 'HORMIGA',
                confidence: 0.7,
                reason: 'Un café preparado por un importe pequeño es un gasto hormiga.',
            };
        }
        return {
            code: 'MARKET',
            confidence: 0.45,
            reason: 'Un «café» suelto es ambiguo: se propone Mercado, confirma la categoría.',
        };
    }
    return null;
}
/** Etiqueta legible de una categoría, con respaldo si el código es desconocido. */
export function categoryLabel(code) {
    return categoryByCode(code)?.name ?? code;
}
/** Color de una categoría, con un gris neutro como respaldo. */
export function categoryColor(code) {
    return categoryByCode(code)?.color ?? '#6E85A6';
}
//# sourceMappingURL=categories.js.map